/*!
 * adcChannel.js — how adc(ch:) spellings map to input channels
 * © 2025 Rob Canning — GPLv3
 *
 * A hand-maintained vocabulary, like ports.js, kept apart from audioAnalysis.js
 * because it is pure: that module builds the whole master audio bus the moment
 * it is imported, and deciding what "left" means should not require an
 * AudioContext to exist.
 *
 * Scores are written by hand, in a text field, often in a hurry before a
 * rehearsal. So every spelling someone might reasonably reach for is accepted,
 * and anything unrecognised falls back to the mixed-down reading rather than
 * listening to a channel that may not be there.
 */

/**
 * @param {string|number|undefined} ch  as written in the cue
 * @returns {number|"mix"} a channel index, or "mix" for no splitter at all
 */
export function parseChannel(ch) {
  if (ch === undefined || ch === null || ch === "") return "mix";
  const t = String(ch).trim().toLowerCase();
  if (t === "mix" || t === "mono" || t === "both") return "mix";
  if (t === "l" || t === "left"  || t === "0") return 0;
  if (t === "r" || t === "right" || t === "1") return 1;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : "mix";
}

/** How a channel reads in a log line. */
export function channelLabel(channel) {
  if (channel === "mix") return "mixed down";
  if (channel === 0) return "left (channel 0)";
  if (channel === 1) return "right (channel 1)";
  return `channel ${channel}`;
}
